/**
 * 🔧 AdminConfigPage - Página de Configurações Administrativas
 * 
 * Página para administradores gerenciarem configurações globais do sistema
 */

import React, { useState, useEffect } from 'react';
import { Card, Button, Form, InputGroup, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { useLocation } from 'react-router-dom';
import MasterLayout from '../../masterLayout/MasterLayout';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import FormModal from '../../components/modals/FormModal';
import ModernSwitch from '../../components/forms/ModernSwitch';
import { API_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import './AdminConfigPage.scss';

// 🎨 Helper: Formatar input de moeda em tempo real
const formatCurrencyInput = (value) => {
  const numbers = value.replace(/\D/g, '');
  const cents = parseInt(numbers) || 0;
  const reais = (cents / 100).toFixed(2);
  return `R$ ${reais.replace('.', ',')}`;
};

// 🔢 Helper: Extrair centavos de string formatada
const parseCurrencyInput = (value) => {
  const numbers = value.replace(/\D/g, '');
  return parseInt(numbers) || 0;
};

// 📊 Helper: Formatar valor do histórico (JSON para legível)
const formatHistoryValue = (value) => {
  if (!value || value === '-') return '-';
  
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    
    // Se tem percentage_fee e fixed_fee_cents (Taxa da Plataforma)
    if (parsed.percentage_fee !== undefined && parsed.fixed_fee_cents !== undefined) {
      const percentual = parsed.percentage_fee;
      const fixo = CurrencyFormatter.format(parsed.fixed_fee_cents);
      return `${percentual}% + ${fixo}`;
    }
    
    // Se tem only percentage_fee
    if (parsed.percentage_fee !== undefined) {
      return `${parsed.percentage_fee}%`;
    }
    
    // Se tem only fixed_fee_cents
    if (parsed.fixed_fee_cents !== undefined) {
      return CurrencyFormatter.format(parsed.fixed_fee_cents);
    }
    
    // Se é um valor em centavos simples (number)
    if (typeof parsed === 'number') {
      return CurrencyFormatter.format(parsed);
    }
    
    // Se é objeto complexo, mostrar resumo
    if (typeof parsed === 'object') {
      const keys = Object.keys(parsed);
      return keys.map(key => `${key}: ${parsed[key]}`).join(', ');
    }
    
    return value;
  } catch (e) {
    // Se não for JSON, retornar como está
    return value;
  }
};

const AdminConfigPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 🔍 Detectar se deve mostrar apenas períodos (rota /admin/config)
  const showOnlyPeriods = location.pathname === '/admin/config';
  
  // 💾 Estados de salvamento separados
  const [savingPlatformFee, setSavingPlatformFee] = useState(false);
  const [savingMinWithdrawal, setSavingMinWithdrawal] = useState(false);
  const [savingAdvance, setSavingAdvance] = useState(false);
  const [savingPeriod, setSavingPeriod] = useState(false);
  
  // 💰 Estados para configurações
  const [platformFee, setPlatformFee] = useState({
    fixed_fee_cents: 1500,
    percentage_fee: 2.5
  });
  
  const [minimumWithdrawal, setMinimumWithdrawal] = useState({
    amount_cents: 1000
  });

  const [advancePayment, setAdvancePayment] = useState({
    enabled: true,
    percentage_fee: 3.5,
    min_days: 1,
    max_days: 25
  });

  // 📅 Estados para períodos de saque
  const [withdrawalPeriods, setWithdrawalPeriods] = useState([]);
  const [changeHistory, setChangeHistory] = useState([]);
  
  // 🔐 Estados de confirmação
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  
  // 📋 Estados de modals
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [periodForm, setPeriodForm] = useState({
    plan_code: 'D+30',
    release_days: 30,
    plan_name: '',
    plan_description: '',
    processing_fee_percentage: 0,
    is_active: true
  });

  // 💾 Estados para valores formatados (UX)
  const [platformFeeDisplay, setPlatformFeeDisplay] = useState('R$ 15,00');
  const [minWithdrawalDisplay, setMinWithdrawalDisplay] = useState('R$ 10,00');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Acesso negado. Apenas administradores podem acessar esta página.');
      setLoading(false);
      return;
    }
    if (user?.role === 'admin') {
      loadConfigurations();
      loadChangeHistory();
    }
  }, [user]);

  /**
   * 📊 Carregar configurações
   */
  const loadConfigurations = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar configurações');
      }

      const result = await response.json();
      
      if (result.success && result.data) {
        // Taxa da plataforma
        if (result.data.platform_fee?.value) {
          const fee = result.data.platform_fee.value;
          setPlatformFee(fee);
          setPlatformFeeDisplay(formatCurrencyInput(fee.fixed_fee_cents.toString()));
        }

        // Valor mínimo de saque
        if (result.data.system_configurations?.minimum_withdrawal_amount?.value) {
          const minValue = result.data.system_configurations.minimum_withdrawal_amount.value;
          setMinimumWithdrawal(minValue);
          setMinWithdrawalDisplay(formatCurrencyInput(minValue.amount_cents.toString()));
        }

        // Antecipação
        if (result.data.system_configurations?.advance_payment_settings?.value) {
          setAdvancePayment(result.data.system_configurations.advance_payment_settings.value);
        }

        // Períodos de saque
        if (result.data.withdrawal_periods) {
          setWithdrawalPeriods(result.data.withdrawal_periods);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
      setError(err.message || 'Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 📜 Carregar histórico de alterações
   */
  const loadChangeHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/config/history`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setChangeHistory(result.data);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    }
  };

  /**
   * 🔐 Confirmar ação antes de salvar
   */
  const requestConfirmation = (message, action) => {
    setConfirmMessage(message);
    setConfirmAction(() => action);
    setShowConfirmModal(true);
  };

  /**
   * 💾 Salvar Taxa da Plataforma
   */
  const savePlatformFee = async () => {
    try {
      setSavingPlatformFee(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/config/platform-fee`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(platformFee)
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar taxa da plataforma');
      }

      setSuccess('Taxa da plataforma atualizada com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      loadChangeHistory();
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Erro ao salvar taxa:', err);
      setError(err.message || 'Erro ao salvar taxa da plataforma');
    } finally {
      setSavingPlatformFee(false);
    }
  };

  /**
   * 💾 Salvar Valor Mínimo de Saque
   */
  const saveMinimumWithdrawal = async () => {
    try {
      setSavingMinWithdrawal(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/config/minimum-withdrawal`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(minimumWithdrawal)
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar valor mínimo de saque');
      }

      setSuccess('Valor mínimo de saque atualizado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      loadChangeHistory();
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Erro ao salvar valor mínimo:', err);
      setError(err.message || 'Erro ao salvar valor mínimo de saque');
    } finally {
      setSavingMinWithdrawal(false);
    }
  };

  /**
   * 💾 Salvar Antecipação
   */
  const saveAdvancePayment = async () => {
    try {
      setSavingAdvance(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/config/advance-payment`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(advancePayment)
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar configurações de antecipação');
      }

      setSuccess('Configurações de antecipação atualizadas com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      loadChangeHistory();
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Erro ao salvar antecipação:', err);
      setError(err.message || 'Erro ao salvar configurações de antecipação');
    } finally {
      setSavingAdvance(false);
    }
  };

  /**
   * 📅 Abrir modal de período (novo ou editar)
   */
  const openPeriodModal = (period = null) => {
    if (period) {
      setEditingPeriod(period);
      setPeriodForm({
        plan_code: period.plan_code,
        release_days: period.release_days,
        plan_name: period.plan_name || '',
        plan_description: period.plan_description || '',
        processing_fee_percentage: period.processing_fee_percentage || 0,
        is_active: period.is_active !== false
      });
    } else {
      setEditingPeriod(null);
      setPeriodForm({
        plan_code: 'D+30',
        release_days: 30,
        plan_name: '',
        plan_description: '',
        processing_fee_percentage: 0,
        is_active: true
      });
    }
    setShowPeriodModal(true);
  };

  /**
   * 💾 Salvar período (criar ou editar)
   */
  const savePeriod = async () => {
    try {
      setSavingPeriod(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/config/withdrawal-period`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(periodForm)
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar período');
      }

      setSuccess(editingPeriod ? 'Período atualizado com sucesso!' : 'Período criado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      setShowPeriodModal(false);
      loadConfigurations();
      loadChangeHistory();
    } catch (err) {
      console.error('Erro ao salvar período:', err);
      setError(err.message || 'Erro ao salvar período');
    } finally {
      setSavingPeriod(false);
    }
  };

  /**
   * ❌ Desativar período
   */
  const deactivatePeriod = async (planCode) => {
    try {
      setSavingPeriod(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/config/withdrawal-period/${planCode}/deactivate`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao desativar período');
      }

      setSuccess('Período desativado com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      loadConfigurations();
      loadChangeHistory();
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Erro ao desativar período:', err);
      setError(err.message || 'Erro ao desativar período');
    } finally {
      setSavingPeriod(false);
    }
  };

  // ⭐ Marcar período como padrão
  const setDefaultPeriod = async (planCode) => {
    try {
      setSavingPeriod(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/config/withdrawal-period/set-default`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ plan_code: planCode })
      });

      if (!response.ok) {
        throw new Error('Erro ao definir período padrão');
      }

      const result = await response.json();
      setSuccess(result.message || 'Período padrão definido com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      loadConfigurations();
      loadChangeHistory();
      setShowConfirmModal(false);
    } catch (err) {
      console.error('Erro ao definir período padrão:', err);
      setError(err.message || 'Erro ao definir período padrão');
    } finally {
      setSavingPeriod(false);
    }
  };

  /**
   * 🎨 Handlers de input com máscara de moeda
   */
  const handlePlatformFeeChange = (e) => {
    const formatted = formatCurrencyInput(e.target.value);
    const cents = parseCurrencyInput(e.target.value);
    setPlatformFeeDisplay(formatted);
    setPlatformFee({ ...platformFee, fixed_fee_cents: cents });
  };

  const handleMinWithdrawalChange = (e) => {
    const formatted = formatCurrencyInput(e.target.value);
    const cents = parseCurrencyInput(e.target.value);
    setMinWithdrawalDisplay(formatted);
    setMinimumWithdrawal({ amount_cents: cents });
  };

  if (loading) {
    return (
      <MasterLayout>
        <div className="dashboard-main-body">
          <div className="container-fluid p-4">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
          </div>
        </div>
      </MasterLayout>
    );
  }

  if (error && user?.role !== 'admin') {
    return (
      <MasterLayout>
        <div className="dashboard-main-body">
          <div className="container-fluid p-4">
            <Alert variant="danger">
              <Icon icon="mdi:alert-circle" className="me-2" />
              {error}
            </Alert>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <div className="dashboard-main-body">
        <div className="container-fluid p-4">
          <AdminPageHeader
            icon={showOnlyPeriods ? "mdi:calendar-clock" : "solar:settings-bold"}
            title={showOnlyPeriods ? "Períodos de Liberação" : "Configurações do Sistema"}
            subtitle={showOnlyPeriods ? "Gerencie os períodos de liberação de saque que podem ser atribuídos aos produtores" : "Gerencie taxas da plataforma, valor mínimo de saque, antecipação e períodos de liberação"}
          />

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <Icon icon="mdi:alert-circle" className="me-2" />
              {error}
            </Alert>
          )}

          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              <Icon icon="mdi:check-circle" className="me-2" />
              {success}
            </Alert>
          )}

          {!showOnlyPeriods && (
          <div className="row mt-4">
            {/* 💰 Taxa da Plataforma */}
            <div className="col-md-4 mb-4">
              <Card style={{ height: '100%' }}>
                <Card.Header className="d-flex align-items-center">
                  <Icon icon="mdi:currency-usd" className="me-2" style={{ fontSize: '24px' }} />
                  <span style={{ fontSize: '20px', fontWeight: '600', display: 'block' }}>Taxa da Plataforma</span>
                </Card.Header>
                <Card.Body className="d-flex flex-column">
                  <Form className="flex-grow-1">
                    <Form.Group className="mb-3">
                      <Form.Label>Taxa Fixa</Form.Label>
                      <Form.Control
                        type="text"
                        value={platformFeeDisplay}
                        onChange={handlePlatformFeeChange}
                        placeholder="R$ 15,00"
                      />
                      <Form.Text className="text-muted">
                        Valor fixo cobrado por transação
                      </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Taxa Percentual</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={platformFee.percentage_fee}
                          onChange={(e) => setPlatformFee({ ...platformFee, percentage_fee: parseFloat(e.target.value) || 0 })}
                          placeholder="2.5"
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                      <Form.Text className="text-muted">
                        Percentual cobrado sobre o valor da transação
                      </Form.Text>
                    </Form.Group>
                  </Form>

                  <Button 
                    variant="primary" 
                    onClick={() => requestConfirmation(
                      'Esta alteração afetará todas as novas transações. Deseja continuar?',
                      savePlatformFee
                    )} 
                    disabled={savingPlatformFee}
                    className="mt-auto"
                  >
                    {savingPlatformFee ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:content-save" className="me-2" />
                        Salvar Taxa
                      </>
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </div>

            {/* 💰 Valor Mínimo de Saque */}
            <div className="col-md-4 mb-4">
              <Card style={{ height: '100%' }}>
                <Card.Header className="d-flex align-items-center">
                  <Icon icon="mdi:cash-multiple" className="me-2" style={{ fontSize: '24px' }} />
                  <span style={{ fontSize: '20px', fontWeight: '600', display: 'block' }}>Valor Mínimo de Saque</span>
                </Card.Header>
                <Card.Body className="d-flex flex-column">
                  <Form className="flex-grow-1">
                    <Form.Group className="mb-3">
                      <Form.Label>Valor Mínimo</Form.Label>
                      <Form.Control
                        type="text"
                        value={minWithdrawalDisplay}
                        onChange={handleMinWithdrawalChange}
                        placeholder="R$ 10,00"
                      />
                      <Form.Text className="text-muted">
                        Valor mínimo que um usuário pode solicitar para saque
                      </Form.Text>
                    </Form.Group>
                  </Form>

                  <Button 
                    variant="primary" 
                    onClick={() => requestConfirmation(
                      'Esta alteração afetará todas as novas solicitações de saque. Deseja continuar?',
                      saveMinimumWithdrawal
                    )} 
                    disabled={savingMinWithdrawal}
                    className="mt-auto"
                  >
                    {savingMinWithdrawal ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:content-save" className="me-2" />
                        Salvar Valor Mínimo
                      </>
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </div>

            {/* 🚀 Antecipação */}
            <div className="col-md-4 mb-4">
              <Card style={{ height: '100%' }}>
                <Card.Header className="d-flex align-items-center">
                  <Icon icon="mdi:lightning-bolt" className="me-2" style={{ fontSize: '24px' }} />
                  <span style={{ fontSize: '20px', fontWeight: '600', display: 'block' }}>Antecipação</span>
                </Card.Header>
                <Card.Body className="d-flex flex-column">
                  <Form className="flex-grow-1">
                    <Form.Group className="mb-3">
                      <ModernSwitch
                        label="Antecipação Habilitada"
                        checked={advancePayment.enabled}
                        onChange={(newValue) => setAdvancePayment({ ...advancePayment, enabled: newValue })}
                        size="md"
                        color="blue"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Taxa Percentual</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={advancePayment.percentage_fee}
                          onChange={(e) => setAdvancePayment({ ...advancePayment, percentage_fee: parseFloat(e.target.value) || 0 })}
                          placeholder="3.5"
                          disabled={!advancePayment.enabled}
                        />
                        <InputGroup.Text>%</InputGroup.Text>
                      </InputGroup>
                    </Form.Group>

                    <div className="row">
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label>Dias Mínimo</Form.Label>
                          <Form.Control
                            type="number"
                            value={advancePayment.min_days}
                            onChange={(e) => setAdvancePayment({ ...advancePayment, min_days: parseInt(e.target.value) || 0 })}
                            placeholder="1"
                            disabled={!advancePayment.enabled}
                          />
                        </Form.Group>
                      </div>
                      <div className="col-md-6">
                        <Form.Group className="mb-3">
                          <Form.Label>Dias Máximo</Form.Label>
                          <Form.Control
                            type="number"
                            value={advancePayment.max_days}
                            onChange={(e) => setAdvancePayment({ ...advancePayment, max_days: parseInt(e.target.value) || 0 })}
                            placeholder="25"
                            disabled={!advancePayment.enabled}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  </Form>

                  <Button 
                    variant="primary" 
                    onClick={() => requestConfirmation(
                      'Esta alteração afetará todas as solicitações de antecipação. Deseja continuar?',
                      saveAdvancePayment
                    )} 
                    disabled={savingAdvance}
                    className="mt-auto"
                  >
                    {savingAdvance ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Icon icon="mdi:content-save" className="me-2" />
                        Salvar Antecipação
                      </>
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </div>
          </div>
          )}

          {/* 📅 Períodos de Saque */}
          <div className="row mt-4">
            <div className="col-12">
              <Card>
                <Card.Header className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <Icon icon="mdi:calendar-clock" className="me-2" style={{ fontSize: '24px' }} />
                    <span style={{ fontSize: '20px', fontWeight: '600', display: 'block' }}>Períodos de Liberação</span>
                  </div>
                  <Button variant="primary" onClick={() => openPeriodModal()}>
                    <Icon icon="mdi:plus" className="me-2" />
                    Novo Período
                  </Button>
                </Card.Header>
                <Card.Body>
                  {withdrawalPeriods.length === 0 ? (
                    <Alert variant="info">
                      <Icon icon="mdi:information" className="me-2" />
                      Nenhum período cadastrado. Clique em "Novo Período" para adicionar.
                    </Alert>
                  ) : (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Código</th>
                          <th>Dias</th>
                          <th>Nome</th>
                          <th>Taxa (%)</th>
                          <th>Status</th>
                          <th className="text-center">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdrawalPeriods.map((period) => (
                          <tr key={period.plan_code}>
                            <td>
                              <strong>{period.plan_code}</strong>
                              {period.is_default && (
                                <Badge bg="warning" text="dark" className="ms-2">
                                  ⭐ Padrão
                                </Badge>
                              )}
                            </td>
                            <td>{period.release_days} dias</td>
                            <td>{period.plan_name || '-'}</td>
                            <td>{period.processing_fee_percentage}%</td>
                            <td>
                              {period.is_active ? (
                                <Badge bg="success">Ativo</Badge>
                              ) : (
                                <Badge bg="secondary">Inativo</Badge>
                              )}
                            </td>
                            <td className="text-center">
                              {!period.is_default && (
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => requestConfirmation(
                                    `Deseja marcar ${period.plan_code} como período padrão? Todos os novos usuários usarão este período automaticamente.`,
                                    () => setDefaultPeriod(period.plan_code)
                                  )}
                                  title="Marcar como padrão"
                                >
                                  <Icon icon="mdi:star" />
                                </Button>
                              )}
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                                onClick={() => openPeriodModal(period)}
                              >
                                <Icon icon="mdi:pencil" />
                              </Button>
                              {period.is_active && (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => requestConfirmation(
                                    `Deseja desativar o período ${period.plan_code}? Isso afetará todos os produtores que usam este período.`,
                                    () => deactivatePeriod(period.plan_code)
                                  )}
                                >
                                  <Icon icon="mdi:close" />
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>

          {/* 📜 Histórico de Alterações */}
          {changeHistory.length > 0 && (
            <div className="row mt-4">
              <div className="col-12">
                <Card>
                  <Card.Header className="d-flex align-items-center">
                    <Icon icon="mdi:history" className="me-2" style={{ fontSize: '24px' }} />
                    <span style={{ fontSize: '20px', fontWeight: '600', display: 'block' }}>Histórico de Alterações</span>
                  </Card.Header>
                  <Card.Body>
                    <Table responsive hover size="sm">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Usuário</th>
                          <th>Configuração</th>
                          <th>Ação</th>
                          <th>Valor Anterior</th>
                          <th>Novo Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {changeHistory.slice(0, 10).map((change, idx) => (
                          <tr key={idx}>
                            <td>{new Date(change.created_at).toLocaleString('pt-BR')}</td>
                            <td>{change.user_name}</td>
                            <td>{change.config_type}</td>
                            <td>
                              <Badge bg="primary">{change.action}</Badge>
                            </td>
                            <td>{formatHistoryValue(change.old_value)}</td>
                            <td>{formatHistoryValue(change.new_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 🔐 Modal de Confirmação */}
      <ConfirmationModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={confirmAction}
        title="Confirmar Alteração"
        message={confirmMessage}
        confirmText="Confirmar"
        cancelText="Cancelar"
        type="warning"
        icon="mdi:alert"
        loading={savingPlatformFee || savingMinWithdrawal || savingAdvance || savingPeriod}
        loadingText="Salvando..."
      />

      {/* 📋 Modal de Período */}
      <FormModal
        show={showPeriodModal}
        onHide={() => setShowPeriodModal(false)}
        onSave={savePeriod}
        title={editingPeriod ? 'Editar Período' : 'Novo Período'}
        titleIcon="mdi:calendar-clock"
        size="lg"
        loading={savingPeriod}
        loadingText="Salvando..."
        saveText={editingPeriod ? 'Atualizar' : 'Criar'}
      >
        <Form>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Código do Plano</Form.Label>
                <Form.Control
                  type="text"
                  value={periodForm.plan_code}
                  onChange={(e) => setPeriodForm({ ...periodForm, plan_code: e.target.value })}
                  placeholder="D+30"
                  disabled={!!editingPeriod}
                />
                <Form.Text className="text-muted">
                  Exemplo: D+7, D+15, D+30, D+60
                </Form.Text>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Dias para Liberação</Form.Label>
                <Form.Control
                  type="number"
                  value={periodForm.release_days}
                  onChange={(e) => setPeriodForm({ ...periodForm, release_days: parseInt(e.target.value) || 0 })}
                  placeholder="30"
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Nome do Plano</Form.Label>
            <Form.Control
              type="text"
              value={periodForm.plan_name}
              onChange={(e) => setPeriodForm({ ...periodForm, plan_name: e.target.value })}
              placeholder="Plano Mensal"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={periodForm.plan_description}
              onChange={(e) => setPeriodForm({ ...periodForm, plan_description: e.target.value })}
              placeholder="Descrição do período"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Taxa de Processamento (%)</Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                step="0.01"
                value={periodForm.processing_fee_percentage}
                onChange={(e) => setPeriodForm({ ...periodForm, processing_fee_percentage: parseFloat(e.target.value) || 0 })}
                placeholder="0"
              />
              <InputGroup.Text>%</InputGroup.Text>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <ModernSwitch
              label="Período Ativo"
              checked={periodForm.is_active}
              onChange={(newValue) => setPeriodForm({ ...periodForm, is_active: newValue })}
              size="md"
              color="blue"
            />
          </Form.Group>
        </Form>
      </FormModal>
    </MasterLayout>
  );
};

export default AdminConfigPage;
